import { useState } from 'react';

import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faCopy, faDownload, faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const VideoPlayer = ({ video, slug, id, baseUrl, loading }) => {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const response = await fetch(`${baseUrl}/arquivos/${slug}/videos/${id}/download`, {
                method: 'GET',
            });

            if (!response.ok) throw new Error('Erro ao buscar o link de download');

            const data = await response.json();
            if (data.error) throw new Error(data.error);

            const fileResponse = await fetch(data.download_url);
            const blob = await fileResponse.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `${video.produto_nome} - ${video.nome}.mp4`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(blobUrl);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleShare = async () => {
        const shareUrl = `${window.location.origin}/arquivos/${slug}/videos/${id}`;
        const shareText = `Assista a esse vídeo da Rometal: ${video.nome}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Compartilhar',
                    text: shareText,
                    url: shareUrl,
                });
            } catch (error) {
                console.error('Erro ao compartilhar:', error);
            }
        } else {
            alert('O compartilhamento não é suportado neste navegador.');
        }
    };

    const handleCopyYoutubeLink = async () => {
        try {
            await navigator.clipboard.writeText(video.youtube_link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Erro ao copiar link:', error);
        }
    };

    if (loading)
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-10 opacity-20 mt-10">
                {[...Array(4)].map((_, index) => (
                    <div
                        key={index}
                        role="status"
                        className="flex items-center justify-center h-0 pb-[90%] md:pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                    >
                        <span className="sr-only">Loading...</span>
                    </div>
                ))}
            </div>
        );

    return (
        <>
            {video.embed_url && (
                <div className="relative w-full max-w-4xl h-0 pb-[57%] lg:pb-[36%] -mt-10">
                    <iframe
                        title="Video Player"
                        src={`${video.embed_url}&amp;title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                        allowFullScreen
                        className="absolute inset-0"
                    />
                </div>
            )}

            <div className="flex max-md:justify-center flex-wrap items-center gap-4 mt-3 mb-20">
                {video.baixavel && (
                    <button
                        className="group flex items-center border-2 border-secondary rounded-md text-white font-semibold uppercase max-sm:text-sm sm:tracking-wide px-4 py-2 transition-all hover:bg-secondary disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleDownload}
                        disabled={downloadLoading}
                    >
                        {downloadLoading ? (
                            <svg
                                className="animate-spin mr-2 h-4 w-4 text-secondary group-hover:text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                        ) : (
                            <FontAwesomeIcon icon={faDownload} className="mr-2 text-secondary transition-all group-hover:text-white" />
                        )}
                        {downloadLoading ? 'Baixando...' : 'Download'}
                    </button>
                )}
                {video.compartilhavel && (
                    <button
                        className="group flex items-center border-2 border-secondary rounded-md text-white font-semibold uppercase max-sm:text-sm sm:tracking-wide px-3 py-2 transition-all hover:bg-secondary"
                        onClick={handleShare}
                    >
                        <FontAwesomeIcon icon={faShare} className="mr-2 text-secondary transition-all group-hover:text-white" />
                        Compartilhar
                    </button>
                )}
                {video.youtube_link && (
                    <div className="flex items-center gap-2 border-2 border-secondary rounded-md px-4 py-1 bg-white w-full max-w-[350px]">
                        <FontAwesomeIcon icon={faYoutube} className="mr-2 text-primary transition-all group-hover:text-white" />
                        <input
                            type="text"
                            name="youtube_link"
                            className="text-sm w-full focus-visible:outline-0 focus-visible:outline-white"
                            value={video.youtube_link}
                            readOnly
                        />
                        <div className="relative">
                            <button
                                onClick={handleCopyYoutubeLink}
                                className="ml-2 -mr-2 py-1 px-3 rounded-md bg-secondary hover:bg-tertiary text-white transition-all"
                                title="Copiar link"
                            >
                                <FontAwesomeIcon icon={faCopy} className="text-sm" />
                            </button>
                            {copied && (
                                <span className="animate-fade-in-down absolute ml-2 -top-8 left-1/2 !-translate-x-1/2 bg-secondary text-white text-xs font-semibold px-2 py-1 rounded-md whitespace-nowrap shadow-md animate-fade-in">
                                    Copiado!
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default VideoPlayer;
